import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ControlledFormDatePicker } from '@components/formInputs/ControlledFormDatePicker';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormTimeSelect } from '@components/formInputs/ControlledFormTimeSelect';
import { Button, ButtonVariants } from '@components/button/Button';
import { FloorPlanTableData } from '@shared/types/floorPlans';
import styles from './MultiTableBookingForm.scss';
import { Icon } from '@components/icon/Icon';
import typography from '~styles/typography.scss';
import { ServiceReservation } from '../service/apiHelpers';
import { MultiTableReservationData } from './types';

export type MultiTableReservationFormData = Omit<
  MultiTableReservationData,
  'floorPlanTableIds'
>;

interface MultiTableBookingFormProps {
  selectedFloorPlanTables: FloorPlanTableData[];
  handleSubmitClick: (formData: MultiTableReservationFormData) => void;
  tableLabelPosition: 'top' | 'bottom';
  reservation?: ServiceReservation;
  submitButtonText: string;
  onCancel?: () => void;
  defaultDate?: string;
  defaultTime?: string;
  defaultDuration?: number;
  onSuccess?: (formData: MultiTableReservationFormData) => void; // Callback for successful submission
}

export const MultiTableBookingForm = ({
  selectedFloorPlanTables,
  handleSubmitClick,
  tableLabelPosition,
  reservation,
  submitButtonText,
  onCancel,
  defaultDate,
  defaultTime,
  defaultDuration,
  onSuccess,
}: MultiTableBookingFormProps) => {
  const defaultValues = reservation
    ? {
        date: reservation.date,
        time: reservation.time,
        guestCount: reservation.guestCount,
        duration: reservation.duration as number,
      }
    : {
        date: defaultDate || '',
        time: defaultTime || '',
        guestCount: 0,
        duration: defaultDuration || 0,
      };

  const { control, handleSubmit, watch } =
    useForm<MultiTableReservationFormData>({
      defaultValues,
    });

  const [date, time, guestCount, duration] = watch([
    'date',
    'time',
    'guestCount',
    'duration',
  ]);

  const isFormComplete =
    !!date &&
    !!time &&
    Number(guestCount) > 0 &&
    Number(duration) > 0 &&
    selectedFloorPlanTables.length > 0;
  const floorPlanTableNames = selectedFloorPlanTables
    .map((table) => table.name)
    .join(', ');

  const onSubmit: SubmitHandler<MultiTableReservationFormData> = (
    formValues,
  ) => {
    // Call the original handler
    handleSubmitClick(formValues);

    // Call the success callback if provided
    if (onSuccess) {
      onSuccess(formValues);
    }
  };

  const tableLabel = (
    <div className={styles.floorPlanTableCountContainer}>
      <Icon name="combinedTable" />
      <div className={styles.floorPlanTableCount}>
        {selectedFloorPlanTables.length === 0 && (
          <span className={typography.c3_20}>
            Select 1 or more tables from the floor plan
          </span>
        )}

        {selectedFloorPlanTables.length > 0 && (
          <span className={typography.c3_20}>{floorPlanTableNames}</span>
        )}
      </div>
    </div>
  );

  return (
    <form
      className={styles.reservationDetailsForm}
      onSubmit={handleSubmit(onSubmit)}
      aria-label="Large Party Reservation Details"
    >
      {tableLabelPosition === 'top' && tableLabel}

      <ControlledFormDatePicker
        control={control}
        name="date"
        label="Date"
        rules={{ required: true }}
      />

      <ControlledFormTimeSelect
        control={control}
        name="time"
        label="Start Time"
        rules={{ required: true }}
      />

      <ControlledFormInput
        control={control}
        name="guestCount"
        label="Guest Count"
        type="number"
        rules={{ required: true }}
      />

      <ControlledFormInput
        control={control}
        name="duration"
        label="Duration (in minutes)"
        type="number"
        rules={{ required: true }}
      />

      {tableLabelPosition === 'bottom' && tableLabel}

      <Button
        type="submit"
        label={submitButtonText}
        variant={ButtonVariants.Primary}
        isDisabled={!isFormComplete}
      />
      {onCancel && (
        <Button
          label="Cancel"
          variant={ButtonVariants.Tertiary}
          onClick={onCancel}
        />
      )}
    </form>
  );
};
